/**
 * Schematic metabolic curves for a healthy adult who stops eating at hour 0.
 * These are textbook-style approximations (not measurements) used to show how
 * the body shifts from dietary glucose to liver glycogen to ketones, with
 * insulin falling away to let stored fuel out.
 */

export type PhysiologyMetricKey = 'bloodGlucose' | 'insulin' | 'liverGlycogen' | 'ketones'

type Keypoint = readonly [hours: number, value: number]

export type PhysiologyMetric = {
   key: PhysiologyMetricKey
   unit: string
   min: number
   max: number
   decimals: number
   keypoints: readonly Keypoint[]
}

export const PHYSIOLOGY_METRICS: readonly PhysiologyMetric[] = [
   {
      // Post-meal peak, back to baseline by ~3h, then a slow drift down as
      // gluconeogenesis keeps it in the healthy range.
      key: 'bloodGlucose',
      unit: 'mg/dL',
      min: 60,
      max: 140,
      decimals: 0,
      keypoints: [
         [0, 95],
         [1, 132],
         [2, 108],
         [3, 92],
         [8, 88],
         [12, 85],
         [18, 82],
         [24, 79],
         [36, 75],
         [48, 72],
         [72, 68],
      ],
   },
   {
      // Fasting serum insulin; peaks with the post-meal glucose rise, is back
      // near baseline by ~3–4h, then keeps sliding lower, which is what unlocks
      // fat release and ketone production.
      key: 'insulin',
      unit: 'µU/mL',
      min: 0,
      max: 50,
      decimals: 0,
      keypoints: [
         [0, 8],
         [1, 45],
         [2, 24],
         [3, 12],
         [5, 8],
         [12, 6],
         [18, 5],
         [24, 4],
         [36, 3.5],
         [48, 3],
         [72, 2.5],
      ],
   },
   {
      // Liver glycogen as % of a full store (~100 g); refills from the last
      // meal, then drains steadily with roughly half left at ~24h and near
      // empty by 48–72h (Rothman et al. 1991, 13C NMR during a 68h fast).
      key: 'liverGlycogen',
      unit: '%',
      min: 0,
      max: 100,
      decimals: 0,
      keypoints: [
         [0, 85],
         [3, 100],
         [6, 92],
         [12, 75],
         [18, 60],
         [24, 45],
         [36, 25],
         [48, 12],
         [72, 5],
      ],
   },
   {
      // Blood beta-hydroxybutyrate; nutritional ketosis starts around 0.5 mmol/L.
      // The chart raises this scale ceiling as longer fasts come into view.
      key: 'ketones',
      unit: 'mmol/L',
      min: 0,
      max: 1,
      decimals: 1,
      keypoints: [
         [0, 0.1],
         [8, 0.1],
         [12, 0.15],
         [16, 0.25],
         [20, 0.35],
         [24, 0.5],
         [36, 1],
         [48, 1.5],
         [72, 2.5],
      ],
   },
]

/**
 * Linearly interpolates a metric's modeled value at a point in the fast,
 * holding the last keypoint flat beyond the modeled range.
 *
 * @param metric - The metric to sample.
 * @param hours - Hours since the fast started.
 * @returns The modeled value.
 */
export function getPhysiologyValue(metric: PhysiologyMetric, hours: number): number {
   const safeHours = Math.max(0, hours)
   let previous: Keypoint | null = null
   for (const point of metric.keypoints) {
      const [pointHours, pointValue] = point
      if (pointHours >= safeHours) {
         if (!previous) {
            return pointValue
         }
         const [startHours, startValue] = previous
         const ratio = (safeHours - startHours) / (pointHours - startHours)
         return startValue + (pointValue - startValue) * ratio
      }
      previous = point
   }
   return previous ? previous[1] : 0
}
